<template>
  <div>
    <div v-if="mensaje" class="alert alert-success alert-dismissible">
      {{ mensaje }}
      <button type="button" class="btn-close" @click="mensaje = ''"></button>
    </div>
    <div v-if="aviso" class="alert alert-warning alert-dismissible">
      {{ aviso }}
      <button type="button" class="btn-close" @click="aviso = ''"></button>
    </div>
    <div v-if="error" class="alert alert-danger alert-dismissible">
      {{ error }}
      <button type="button" class="btn-close" @click="error = ''"></button>
    </div>

    <div v-if="!notion.configured" class="alert alert-info">
      <i class="bi bi-info-circle me-1"></i>
      Puedes escribir aquí y, cuando quieras, conectar tu base de
      <strong>Reflexiones</strong> de Notion en
      <router-link to="/perfil">Perfil</router-link>
      para sincronizar en ambos sentidos.
    </div>

    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <button type="button" class="btn btn-warning btn-sm" @click="nuevaReflexion">
        <i class="bi bi-plus-lg me-1"></i>
        Nueva reflexión
      </button>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="notion.lastSyncAt" class="text-muted small">
          Última sync: {{ formatearFechaHora(notion.lastSyncAt) }}
        </span>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          :disabled="sincronizando || !notion.configured"
          @click="sincronizar"
        >
          <span v-if="sincronizando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-repeat me-1"></i>
          Sincronizar con Notion
        </button>
      </div>
    </div>

    <div v-if="notion.lastSyncError && notion.lastSyncError !== aviso" class="alert alert-warning small py-2">
      <i class="bi bi-exclamation-triangle me-1"></i>
      {{ notion.lastSyncError }}
    </div>

    <div class="row g-4">
      <div v-if="mostrarPanel" class="col-lg-7">
        <div class="card shadow-sm border-warning">
          <div class="card-header bg-warning text-dark d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div class="d-flex align-items-center gap-2 min-w-0">
              <button
                v-if="esMovil"
                type="button"
                class="btn btn-sm btn-outline-dark"
                @click="cerrarPanel"
              >
                <i class="bi bi-arrow-left me-1"></i>
                Listado
              </button>
              <h5 class="mb-0 text-truncate">
                <i class="bi bi-journal-richtext me-2"></i>
                {{ tituloPanel }}
              </h5>
            </div>
            <span v-if="form.sync_status && form.sync_status !== 'nuevo'" class="badge" :class="badgeClase">
              {{ etiquetaSync }}
            </span>
          </div>
          <div class="card-body">
            <template v-if="modoLectura">
              <div class="mb-3">
                <h3 class="h4 mb-1">{{ form.titulo || 'Sin título' }}</h3>
                <div class="text-muted small">{{ formatearFechaCorta(fecha) }}</div>
              </div>
              <div
                class="pensamiento-lectura"
                v-html="contenidoHtml || '<p class=&quot;text-muted mb-0&quot;>Sin texto</p>'"
              ></div>
              <div class="d-flex gap-2 flex-wrap mt-3">
                <button type="button" class="btn btn-warning" @click="empezarEdicion">
                  <i class="bi bi-pencil me-1"></i>
                  Editar
                </button>
                <a
                  v-if="form.notion_url"
                  :href="form.notion_url"
                  class="btn btn-outline-secondary"
                  target="_blank"
                  rel="noopener"
                >
                  <i class="bi bi-box-arrow-up-right me-1"></i>
                  Abrir en Notion
                </a>
                <button
                  type="button"
                  class="btn btn-outline-danger ms-auto"
                  :disabled="guardando"
                  @click="borrar"
                >
                  <i class="bi bi-trash me-1"></i>
                  Borrar
                </button>
              </div>
            </template>
            <template v-else>
              <div class="mb-3">
                <label class="form-label">Título</label>
                <input
                  v-model="form.titulo"
                  type="text"
                  class="form-control"
                  placeholder="Título de la reflexión"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Fecha</label>
                <input
                  v-model="fecha"
                  type="date"
                  class="form-control"
                />
              </div>
              <label class="form-label">Reflexión</label>
              <PensamientoEditor v-model="form.contenido" />
              <div class="d-flex gap-2 flex-wrap mt-3">
                <button
                  type="button"
                  class="btn btn-warning"
                  :disabled="guardando || !fecha"
                  @click="guardar"
                >
                  <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-save me-1"></i>
                  {{ notion.configured ? 'Guardar y sincronizar' : 'Guardar' }}
                </button>
                <button
                  v-if="form.id"
                  type="button"
                  class="btn btn-outline-secondary"
                  :disabled="guardando"
                  @click="cancelarEdicion"
                >
                  Cancelar
                </button>
                <a
                  v-if="form.notion_url"
                  :href="form.notion_url"
                  class="btn btn-outline-secondary"
                  target="_blank"
                  rel="noopener"
                >
                  <i class="bi bi-box-arrow-up-right me-1"></i>
                  Abrir en Notion
                </a>
                <button
                  v-if="form.id"
                  type="button"
                  class="btn btn-outline-danger ms-auto"
                  :disabled="guardando"
                  @click="borrar"
                >
                  <i class="bi bi-trash me-1"></i>
                  Borrar
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div v-if="mostrarListado" class="col-lg-5" :class="{ 'col-lg-12': !mostrarPanel }">
        <div class="card shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>
              <i class="bi bi-journal-text me-2"></i>
              Todas las reflexiones
              <span class="badge bg-secondary ms-1">{{ listado.length }}</span>
            </span>
          </div>
          <div class="p-2 border-bottom">
            <input
              v-model="busqueda"
              type="search"
              class="form-control form-control-sm"
              placeholder="Buscar por título o texto…"
            />
          </div>
          <div v-if="cargando" class="text-center py-4">
            <div class="spinner-border text-warning"></div>
          </div>
          <div v-else class="list-group list-group-flush pensamiento-lista">
            <button
              v-for="p in filtrados"
              :key="p.id"
              type="button"
              class="list-group-item list-group-item-action"
              :class="{ active: p.id === form.id }"
              @click="seleccionar(p.id)"
            >
              <div class="d-flex justify-content-between gap-2">
                <strong>{{ p.titulo || 'Sin título' }}</strong>
                <small class="text-nowrap">{{ formatearFechaCorta(p.fecha) }}</small>
              </div>
              <div class="small mt-1" :class="p.id === form.id ? '' : 'text-muted'">
                {{ preview(p.contenido) }}
              </div>
            </button>
            <div v-if="!filtrados.length" class="list-group-item text-muted text-center py-4">
              {{ listado.length ? 'Ninguna reflexión coincide con la búsqueda.' : 'Aún no hay reflexiones.' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import axios from '../config/axios';
import PensamientoEditor from './PensamientoEditor.vue';
import { markdownToPlain, markdownToReadingHtml } from '../utils/pensamientosRich';

function apiBase() {
  return axios.defaults.baseURL || '';
}

function toMediaSrc(src) {
  const raw = String(src || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${apiBase()}${raw}`;
}

export default {
  name: 'PensamientosTab',
  components: { PensamientoEditor },
  setup() {
    const route = useRoute();
    const cargando = ref(false);
    const guardando = ref(false);
    const sincronizando = ref(false);
    const mensaje = ref('');
    const aviso = ref('');
    const error = ref('');
    const hoy = ref('');
    const fecha = ref('');
    const busqueda = ref('');
    const listado = ref([]);
    const esMovil = ref(false);
    const panelAbierto = ref(false);
    const modoEdicion = ref(false);
    const notion = ref({ configured: false, lastSyncAt: null, lastSyncError: null });
    const form = ref({
      id: null,
      titulo: '',
      contenido: '',
      notion_url: null,
      sync_status: 'nuevo',
      sync_error: null,
    });

    const filtrados = computed(() => {
      const q = busqueda.value.trim().toLowerCase();
      const rows = [...listado.value].sort((a, b) => {
        if (a.fecha !== b.fecha) return a.fecha < b.fecha ? 1 : -1;
        const au = a.updated_at || '';
        const bu = b.updated_at || '';
        if (au !== bu) return au < bu ? 1 : -1;
        return (b.id || 0) - (a.id || 0);
      });
      if (!q) return rows;
      return rows.filter((p) => {
        const haystack = `${p.titulo || ''} ${p.contenido || ''} ${p.fecha || ''}`.toLowerCase();
        return haystack.includes(q);
      });
    });

    const mostrarPanel = computed(() => !esMovil.value || panelAbierto.value);
    const mostrarListado = computed(() => !esMovil.value || !panelAbierto.value);
    const modoLectura = computed(() => !!form.value.id && !modoEdicion.value);
    const tituloPanel = computed(() => {
      if (modoLectura.value) return form.value.titulo || 'Reflexión';
      return form.value.id ? 'Editar reflexión' : 'Nueva reflexión';
    });
    const contenidoHtml = computed(() => markdownToReadingHtml(form.value.contenido, toMediaSrc));

    const badgeClase = computed(() => {
      const s = form.value.sync_status;
      if (s === 'synced') return 'bg-success';
      if (s === 'error') return 'bg-danger';
      if (s === 'pending') return 'bg-warning text-dark';
      return 'bg-secondary';
    });
    const etiquetaSync = computed(() => {
      const s = form.value.sync_status;
      if (s === 'synced') return 'En Notion';
      if (s === 'error') return 'Error de sync';
      if (s === 'pending') return 'Pendiente';
      if (s === 'local') return 'Solo local';
      return 'Nuevo';
    });

    function actualizarViewport() {
      esMovil.value = typeof window !== 'undefined' ? window.innerWidth < 992 : false;
    }

    function preview(texto) {
      const t = markdownToPlain(texto);
      if (!t) return 'Sin texto';
      return t.length > 90 ? `${t.slice(0, 90)}…` : t;
    }

    function formatearFechaCorta(ymd) {
      if (!ymd) return '';
      const [y, m, d] = ymd.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    }

    function formatearFechaHora(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
    }

    function aplicarPensamiento(p, ymdFecha) {
      fecha.value = ymdFecha || p?.fecha || hoy.value;
      form.value = {
        id: p?.id || null,
        titulo: p?.titulo || '',
        contenido: p?.contenido || '',
        notion_url: p?.notion_url || null,
        sync_status: p?.sync_status || 'nuevo',
        sync_error: p?.sync_error || null,
      };
    }

    function nuevaReflexion() {
      aplicarPensamiento({
        id: null,
        titulo: '',
        contenido: '',
        sync_status: 'nuevo',
      }, hoy.value);
      modoEdicion.value = true;
      panelAbierto.value = true;
    }

    function empezarEdicion() {
      modoEdicion.value = true;
    }

    function cerrarPanel() {
      panelAbierto.value = false;
      modoEdicion.value = false;
    }

    async function cargarListado() {
      cargando.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/pensamientos');
        hoy.value = data.hoy;
        listado.value = data.pensamientos || [];
        notion.value = data.notion || notion.value;
        if (!fecha.value) fecha.value = data.hoy;
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al cargar los pensamientos';
      } finally {
        cargando.value = false;
      }
    }

    async function seleccionar(id) {
      error.value = '';
      try {
        const { data } = await axios.get(`/api/pensamientos/item/${id}`);
        aplicarPensamiento(data.pensamiento);
        modoEdicion.value = false;
        panelAbierto.value = true;
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al cargar la reflexión';
      }
    }

    async function cancelarEdicion() {
      if (!form.value.id) {
        if (esMovil.value) {
          panelAbierto.value = false;
          modoEdicion.value = false;
        }
        return;
      }
      await seleccionar(form.value.id);
    }

    async function guardar() {
      guardando.value = true;
      error.value = '';
      aviso.value = '';
      mensaje.value = '';
      try {
        const payload = {
          fecha: fecha.value,
          titulo: form.value.titulo,
          contenido: form.value.contenido,
        };
        const { data } = form.value.id
          ? await axios.put(`/api/pensamientos/item/${form.value.id}`, payload, { timeout: 30000 })
          : await axios.post('/api/pensamientos', payload, { timeout: 30000 });
        aplicarPensamiento(data.pensamiento);
        modoEdicion.value = false;
        panelAbierto.value = true;
        if (data.warning) aviso.value = data.warning;
        else mensaje.value = data.synced ? 'Guardado y sincronizado con Notion' : 'Pensamiento guardado';
        await cargarListado();
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al guardar';
      } finally {
        guardando.value = false;
      }
    }

    async function borrar() {
      if (!form.value.id) return;
      if (!confirm('¿Borrar esta reflexión? Si está en Notion, también se archivará allí.')) return;
      guardando.value = true;
      error.value = '';
      mensaje.value = '';
      try {
        await axios.delete(`/api/pensamientos/item/${form.value.id}`);
        mensaje.value = 'Pensamiento borrado';
        if (esMovil.value) {
          aplicarPensamiento({
            id: null,
            titulo: '',
            contenido: '',
            sync_status: 'nuevo',
          }, hoy.value);
          panelAbierto.value = false;
          modoEdicion.value = false;
        } else {
          nuevaReflexion();
        }
        await cargarListado();
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al borrar';
      } finally {
        guardando.value = false;
      }
    }

    async function sincronizar() {
      sincronizando.value = true;
      error.value = '';
      aviso.value = '';
      mensaje.value = '';
      try {
        const { data } = await axios.post('/api/pensamientos/sync', { full: true }, { timeout: 180000 });
        if (data.reason === 'no_configurado') {
          mensaje.value = 'Notion no está configurado todavía.';
        } else {
          mensaje.value = data.message || 'Sincronización completada';
        }
        if (data.errors?.length) {
          aviso.value = data.errors.map((e) => `${e.fecha}: ${e.error}`).join(' · ');
        } else if (data.skippedRemote) {
          aviso.value = `Se omitieron ${data.skippedRemote} páginas (archivadas o sin fecha usable).`;
        }
        await cargarListado();
        if (form.value.id) await seleccionar(form.value.id);
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al sincronizar con Notion';
      } finally {
        sincronizando.value = false;
      }
    }

    onMounted(async () => {
      actualizarViewport();
      window.addEventListener('resize', actualizarViewport);
      await cargarListado();
      if (!esMovil.value || route.query.compartir === 'fotos') {
        nuevaReflexion();
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', actualizarViewport);
    });

    return {
      cargando,
      guardando,
      sincronizando,
      mensaje,
      aviso,
      error,
      fecha,
      busqueda,
      listado,
      filtrados,
      notion,
      form,
      esMovil,
      mostrarPanel,
      mostrarListado,
      modoLectura,
      tituloPanel,
      contenidoHtml,
      badgeClase,
      etiquetaSync,
      preview,
      formatearFechaCorta,
      formatearFechaHora,
      seleccionar,
      nuevaReflexion,
      empezarEdicion,
      cancelarEdicion,
      cerrarPanel,
      guardar,
      borrar,
      sincronizar,
    };
  },
};
</script>

<style scoped>
.pensamiento-lista {
  max-height: 640px;
  overflow-y: auto;
}
.list-group-item.active small,
.list-group-item.active .small {
  color: inherit;
}
.pensamiento-lectura {
  min-height: 160px;
  padding: 4px 2px 8px;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #2b2416;
}
.pensamiento-lectura :deep(p) {
  margin-bottom: 0.75rem;
}
.pensamiento-lectura :deep(h1),
.pensamiento-lectura :deep(h2) {
  font-size: 1.35rem;
  margin: 1rem 0 0.5rem;
}
.pensamiento-lectura :deep(h3) {
  font-size: 1.15rem;
  margin: 0.9rem 0 0.4rem;
}
.pensamiento-lectura :deep(blockquote) {
  border-left: 3px solid #e0c98a;
  margin: 0 0 0.75rem;
  padding: 0.15rem 0 0.15rem 0.9rem;
  color: #5c4a24;
}
.pensamiento-lectura :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin: 0.4rem 0 1rem;
  display: block;
}
.pensamiento-lectura :deep(audio) {
  width: 100%;
  max-width: 420px;
  margin: 0.4rem 0 1rem;
  display: block;
}
</style>
